<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useReferenceStore } from '@/stores/referenceStore';
import contactService, { type Contact, type ContactCreateData } from '@/services/ContactService';

const toast = useToast();
const referenceStore = useReferenceStore();

const props = defineProps<{
    visible: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'contact-created', contact: Contact): void;
}>();

// Form data
const formData = ref<ContactCreateData>({
    name: '',
    email: null,
    phone: null,
    companyId: null,
    notes: null
});

// Validation errors
interface ValidationErrors {
    name?: string;
    email?: string;
    phone?: string;
}

const errors = ref<ValidationErrors>({});
const isSubmitting = ref(false);

// Validation
const validateEmail = (email: string | null): boolean | string => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) || 'Please enter a valid email address';
};

const validatePhone = (phone: string | null): boolean | string => {
    if (!phone) return true; // Phone is optional
    // Allow various phone formats
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
    return phoneRegex.test(phone) || 'Please enter a valid phone number';
};

const isFormValid = computed(() => {
    if (!formData.value.name || formData.value.name.trim().length === 0) {
        return false;
    }
    const emailValid = validateEmail(formData.value.email);
    const phoneValid = validatePhone(formData.value.phone);
    return emailValid === true && phoneValid === true;
});

const validateFields = () => {
    errors.value = {};

    if (!formData.value.name || formData.value.name.trim().length === 0) {
        errors.value.name = 'Name is required';
    }

    const emailError = validateEmail(formData.value.email);
    if (typeof emailError === 'string') {
        errors.value.email = emailError;
    }

    const phoneError = validatePhone(formData.value.phone);
    if (typeof phoneError === 'string') {
        errors.value.phone = phoneError;
    }
};

// Reset form
const resetForm = () => {
    formData.value = {
        name: '',
        email: null,
        phone: null,
        companyId: null,
        notes: null
    };
    errors.value = {};
    isSubmitting.value = false;
};

// Close dialog
const closeDialog = () => {
    resetForm();
    emit('update:visible', false);
};

// Submit form
const submitContact = async () => {
    validateFields();
    if (Object.keys(errors.value).length > 0) {
        return;
    }

    isSubmitting.value = true;

    try {
        const response = await contactService.createContact({
            name: formData.value.name.trim(),
            email: formData.value.email?.trim() || null,
            phone: formData.value.phone?.trim() || null,
            companyId: formData.value.companyId,
            notes: formData.value.notes?.trim() || null
        });

        if (response.success && response.data) {
            toast.add({
                severity: 'success',
                summary: 'Contact Created',
                detail: `Contact "${response.data.name}" has been created.`,
                life: 3000
            });
            emit('contact-created', response.data);
            closeDialog();
        } else {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: response.message || 'Failed to create contact',
                life: 5000
            });
        }
    } catch (error: any) {
        console.error('Error creating contact:', error);
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'An unexpected error occurred',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Fetch companies on mount if not loaded
onMounted(async () => {
    if (referenceStore.companies.length === 0) {
        await referenceStore.fetchCompanies();
    }
});
</script>

<template>
    <Dialog
        :visible="props.visible"
        @update:visible="closeDialog"
        modal
        header="Create New Contact"
        :style="{ width: '450px' }"
        :breakpoints="{ '640px': '90vw' }"
        :draggable="false"
    >
        <form @submit.prevent="submitContact">
            <div class="flex flex-col gap-4">
                <!-- Name (required) -->
                <div class="field">
                    <label for="contact-name" class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">
                        Name <span class="text-red-500">*</span>
                    </label>
                    <InputText
                        id="contact-name"
                        v-model="formData.name"
                        @blur="validateFields"
                        class="w-full"
                        :class="{ 'p-invalid': errors.name }"
                        placeholder="Enter contact name"
                        autofocus
                    />
                    <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                </div>

                <!-- Email -->
                <div class="field">
                    <label for="contact-email" class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">
                        Email
                    </label>
                    <InputText
                        id="contact-email"
                        v-model="formData.email"
                        @blur="validateFields"
                        type="email"
                        class="w-full"
                        :class="{ 'p-invalid': errors.email }"
                        placeholder="email@example.com"
                    />
                    <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
                </div>

                <!-- Phone -->
                <div class="field">
                    <label for="contact-phone" class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">
                        Phone
                    </label>
                    <InputText
                        id="contact-phone"
                        v-model="formData.phone"
                        @blur="validateFields"
                        class="w-full"
                        :class="{ 'p-invalid': errors.phone }"
                        placeholder="(555) 123-4567"
                    />
                    <small v-if="errors.phone" class="p-error">{{ errors.phone }}</small>
                </div>

                <!-- Company -->
                <div class="field">
                    <label for="contact-company" class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">
                        Company
                    </label>
                    <Dropdown
                        id="contact-company"
                        v-model="formData.companyId"
                        :options="referenceStore.companies"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Select a company"
                        class="w-full"
                        showClear
                        filter
                    />
                </div>

                <!-- Notes -->
                <div class="field">
                    <label for="contact-notes" class="font-medium text-surface-900 dark:text-surface-0 mb-2 block">
                        Notes
                    </label>
                    <Textarea
                        id="contact-notes"
                        v-model="formData.notes"
                        rows="3"
                        class="w-full"
                        placeholder="Optional notes about this contact"
                    />
                </div>
            </div>
        </form>

        <template #footer>
            <div class="flex gap-2 justify-end">
                <Button
                    label="Cancel"
                    severity="secondary"
                    @click="closeDialog"
                    :disabled="isSubmitting"
                />
                <Button
                    label="Create Contact"
                    @click="submitContact"
                    :loading="isSubmitting"
                    :disabled="!isFormValid || isSubmitting"
                />
            </div>
        </template>
    </Dialog>
</template>